import { linkOptions } from '@tanstack/react-router';
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { UserType } from '../../../types/UserType';
import { BreadcrumbLink, Breadcrumbs } from '../../cdl/Breadcrumbs/Breadcrumbs';
import { useDrawer } from '../../cdl/Drawer/useDrawer.hook';
import { ButtonGroup } from '../../common/buttons/ButtonGroup';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { Page } from '../../common/Page/Page';
import { Box } from '../../common/ui/Box';
import { EditUser } from '../components/EditUser';
import { PersonalRoleSection } from '../personal/components/PersonalRoleSection';

import { ActivitySection } from './components/ActivitySection';
import { MemberDetailActions } from './components/MemberDetailActions';
import { MfaSection } from './components/mfa/MfaSection';
import { SetNewPasswordPopup } from './components/SetNewPasswordPopup';
import { UserInformation } from './components/UserInformation';
import { useManagementUser } from './hooks/useManagementUser';
import { useMfaAccessControl } from './hooks/useMfaAccessControl';

interface MemberDetailPageProps {
    userId: string;
    groupId?: string;
}

export const MemberDetailPage = ({ userId, groupId }: MemberDetailPageProps) => {
    const role = useRole();
    const { push, pop } = useDrawer();
    const [isNewPasswordPopupOpen, setIsNewPasswordPopupOpen] = useState<boolean>(false);
    const accessControl = useMfaAccessControl(userId);

    const userQuery = useManagementUser(userId);

    useDocumentTitle(
        translate('page.memberDetailPage', {
            name: userQuery.data
                ? userQuery.data.firstname || userQuery.data.lastname
                    ? [userQuery.data.firstname, userQuery.data.lastname].filter(Boolean).join(' ')
                    : userQuery.data.email
                : '',
        })
    );

    const handleEditClick = () => {
        if (userQuery.data) {
            const drawerId = push({
                header: { title: translate('user.editMember') },
                content: <EditUser user={userQuery.data} onSuccess={() => pop(drawerId)} />,
            });
        }
    };

    if (userQuery.isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const buildBreadcrumbLinks = (): BreadcrumbLink[] => {
        const links: BreadcrumbLink[] = [
            { label: translate('global.settings'), linkOptions: linkOptions({ to: '/settings' }) },
        ];

        if (role.isSuperAdmin()) {
            links.push({
                label: translate('members'),
                linkOptions: linkOptions({ to: '/users' }),
            });
        }

        if (role.isCustomer()) {
            links.push({
                label: translate('members'),
                linkOptions: linkOptions({
                    to: '/customer-groups/$groupId/members',
                    params: { groupId },
                }),
            });
        }

        if (role.isSupplier()) {
            links.push({
                label: translate('members'),
                linkOptions: linkOptions({
                    to: '/supplier-groups/$groupId/members',
                    params: { groupId },
                }),
            });
        }

        return links;
    };

    const breadcrumbLinks = buildBreadcrumbLinks();

    const userIsSuperAdmin = userQuery.data?.userType === UserType.ADMIN;
    const isSsoEnabled = userQuery.data?.ssoEnabled;

    const companies = (userQuery.data?.companyAccesses || [])
        .map((companyAccess) => companyAccess?.company)
        .filter((company): company is NonNullable<typeof company> => Boolean(company));

    const group = userQuery.data?.group;

    return (
        <Page>
            <Box display="flex" flexDirection="column" gap={40}>
                <Box display="flex" flexDirection="column" gap={4}>
                    <Breadcrumbs links={breadcrumbLinks} />

                    <Box display="flex" justifyContent="space-between" alignItems="start">
                        {userQuery.isPending ? (
                            <>
                                <Box>
                                    <Box display="flex" alignItems="center" gap={6}>
                                        <Skeleton width={200} height={32} />
                                        <Skeleton width={60} height={20} />
                                    </Box>
                                    <Skeleton width={250} height={24} style={{ marginTop: '8px' }} />
                                </Box>
                                <ButtonGroup>
                                    <Skeleton width={120} height={32} />
                                    <Skeleton width={100} height={32} />
                                </ButtonGroup>
                            </>
                        ) : (
                            <>
                                <UserInformation user={userQuery.data} />
                                <MemberDetailActions
                                    userId={userId}
                                    user={userQuery.data}
                                    onEditClick={handleEditClick}
                                />
                            </>
                        )}
                    </Box>
                </Box>

                <Box display="grid" gridTemplateColumns="auto 25%" gap={48}>
                    <Box display="flex" flexDirection="column" gap={40}>
                        {!userIsSuperAdmin ? (
                            userQuery.isSuccess ? (
                                <PersonalRoleSection
                                    accessLevel={userQuery.data.accessLevel}
                                    companyAccesses={userQuery.data.companyAccesses || []}
                                    companies={companies}
                                    group={group!}
                                />
                            ) : (
                                <Skeleton height="40px" />
                            )
                        ) : null}

                        {userQuery.isSuccess && !isSsoEnabled ? (
                            accessControl.canViewMfa ? (
                                <MfaSection
                                    userId={userQuery.data.id}
                                    accessControl={accessControl}
                                    showAddButton={false}
                                />
                            ) : null
                        ) : userQuery.isPending ? (
                            <Box>
                                <Skeleton height="40px" />
                            </Box>
                        ) : null}
                    </Box>

                    {userQuery.isSuccess ? <ActivitySection user={userQuery.data} /> : <Skeleton height="40px" />}
                </Box>
            </Box>

            {userQuery.isSuccess && !isSsoEnabled ? (
                <SetNewPasswordPopup
                    user={userQuery.data}
                    isOpen={isNewPasswordPopupOpen}
                    onDismiss={() => setIsNewPasswordPopupOpen(false)}
                />
            ) : null}
        </Page>
    );
};
